/*
 * changeFeedClient — Transport für den Change-Feed, ohne React.
 *
 * Lebenszyklus (Konzept: docs/change-feed-plan.md im Root-Repo):
 * Bootstrap/Catch-up über GET /changes, dann fetch-basiertes SSE auf
 * /changes/stream (EventSource kann keine Bearer-Header). Bei Abriss:
 * Backoff mit Jitter, vor jedem Reconnect Catch-up per Cursor — die Outbox
 * ist der Replay-Speicher, der Stream puffert nichts. Nach mehreren
 * SSE-Fehlschlägen in Folge: Polling-Fallback mit periodischem SSE-Retry.
 */

const BACKOFF_MIN_MS = 1000
const BACKOFF_MAX_MS = 30000
const FALLBACK_AFTER_FAILURES = 3
const FALLBACK_POLL_MS = 30000
const SSE_RETRY_AFTER_POLLS = 10
// Kein Frame (auch kein Keepalive) so lange -> Verbindung gilt als tot
// (Server-Heartbeat ist 15s, also 3x Puffer).
const STALL_TIMEOUT_MS = 45000
// Feature serverseitig aus (503) oder Endpoint existiert nicht (404,
// Deploy-Skew): kein Fehler-Backoff, sondern seltener Wiederholungsversuch.
const UNAVAILABLE_RETRY_MS = 300000

export function parseSseFrame(raw) {
  let event = "message"
  const dataLines = []
  for (const line of raw.split("\n")) {
    if (line.startsWith(":")) continue
    if (line.startsWith("event:")) event = line.slice(6).trim()
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""))
  }
  if (!dataLines.length) return null
  try {
    return { event, data: JSON.parse(dataLines.join("\n")) }
  } catch {
    return null
  }
}

// Zerlegt den Byte-Strom an Leerzeilen in Frames. Ein Frame kann an einer
// Chunk-Grenze zerschnitten ankommen — der Rest bleibt im Puffer liegen.
// Zeilenenden werden auf \n normalisiert (SSE erlaubt auch \r\n und \r);
// ein Chunk kann mitten in einem \r\n enden, daher der pendingCR-Puffer.
export class SseFrameParser {
  constructor(onFrame) {
    this.buffer = ""
    this.onFrame = onFrame
    this._pendingCR = false
  }

  push(text) {
    let chunk = (this._pendingCR ? "\r" : "") + text
    this._pendingCR = chunk.endsWith("\r")
    if (this._pendingCR) chunk = chunk.slice(0, -1)
    this.buffer += chunk.replace(/\r\n/g, "\n").replace(/\r/g, "\n")
    let idx
    while ((idx = this.buffer.indexOf("\n\n")) !== -1) {
      const raw = this.buffer.slice(0, idx)
      this.buffer = this.buffer.slice(idx + 2)
      const frame = parseSseFrame(raw)
      if (frame) this.onFrame(frame)
    }
  }
}

function jitteredBackoff(failures) {
  const base = Math.min(BACKOFF_MIN_MS * 2 ** Math.max(0, failures - 1), BACKOFF_MAX_MS)
  return base * (0.8 + Math.random() * 0.4)
}

export default class ChangeFeedClient {
  /**
   * @param {object} deps
   * @param {(since: number|null) => Promise<any>} deps.getChanges
   * @param {(opts: {since: number|null, signal: AbortSignal}) => Promise<Response>} deps.openStream
   * @param {(payload: {changes: Array, reset: boolean}) => void} deps.onChanges
   * @param {(status: string) => void} [deps.onStatus]
   */
  constructor({ getChanges, openStream, onChanges, onStatus }) {
    this._getChanges = getChanges
    this._openStream = openStream
    this._onChanges = onChanges
    this._onStatus = onStatus || (() => {})
    this._running = false
    this._cursor = null
    this._failures = 0
    this._abort = null
    this._wakeResolve = null
    this._kicked = false
    this._unavailable = false
  }

  get cursor() {
    return this._cursor
  }

  start() {
    if (this._running) return
    this._running = true
    this._loop()
  }

  stop() {
    this._running = false
    this._abort?.abort()
    this._wake()
  }

  // Sofort neu verbinden (Token-Wechsel, Tab wieder sichtbar).
  kick() {
    if (!this._running) return
    this._kicked = true
    this._failures = 0
    this._abort?.abort()
    this._wake()
  }

  _wake() {
    this._wakeResolve?.()
    this._wakeResolve = null
  }

  _sleep(ms) {
    return new Promise((resolve) => {
      this._wakeResolve = resolve
      setTimeout(resolve, ms)
    })
  }

  _apply(payload) {
    if (typeof payload?.nextCursor === "number") {
      this._cursor = Math.max(this._cursor ?? 0, payload.nextCursor)
    } else if (typeof payload?.cursor === "number") {
      this._cursor = Math.max(this._cursor ?? 0, payload.cursor)
    }
    const changes = payload?.changes || []
    const reset = payload?.reset === true
    if (changes.length || reset) this._onChanges({ changes, reset })
  }

  async _loop() {
    while (this._running) {
      try {
        this._onStatus("connecting")
        // Bootstrap (ohne Cursor) bzw. Catch-up (mit Cursor) — immer VOR dem
        // Stream, damit die Lücke seit dem letzten Frame geschlossen ist.
        const cursorBefore = this._cursor
        const res = await this._getChanges(this._cursor)
        this._apply(res)
        this._unavailable = false
        if (res?.hasMore) {
          // Schutz vor einem Server, dessen Cursor nicht vorrückt: ohne
          // Fortschritt wäre `continue` ein ungebremster Request-Loop.
          if (this._cursor !== cursorBefore) continue
          throw new Error("change feed cursor did not advance despite hasMore")
        }

        const outcome = await this._streamOnce()
        if (outcome.serverEnded) {
          // expired/bye: reguläres Ende -> sofort neu verbinden
          this._failures = 0
        } else if (outcome.sawFrames) {
          // Gesunde Verbindung riss unerwartet (Deploy, Proxy): kurzer
          // fester Backoff statt Eskalation, aber nie ungebremst.
          this._failures = 1
        } else {
          this._failures += 1
        }
      } catch (err) {
        if (!this._running) return
        if (this._kicked) {
          // Bewusster Abbruch (Tab-Refokus): sofort neu verbinden,
          // zählt nicht als Fehlschlag.
          this._kicked = false
          this._failures = 0
          continue
        }
        if (err?.status === 503 || err?.status === 404) {
          // Feature dunkel (Rollout/Rollback): still bleiben, gelegentlich
          // wieder probieren — heilt sich, sobald der Server es anbietet.
          if (!this._unavailable) {
            this._unavailable = true
            console.warn(`Change feed unavailable (${err.status}) — retrying every 5 min`)
          }
          this._onStatus("unavailable")
          this._failures = 0
          await this._sleep(UNAVAILABLE_RETRY_MS)
          continue
        }
        this._failures += 1
        console.warn(`Change feed connection failed (${this._failures}x):`, err?.message || err)
      }

      if (!this._running) return
      if (this._failures >= FALLBACK_AFTER_FAILURES) {
        await this._pollFallback()
      } else if (this._failures > 0) {
        await this._sleep(jitteredBackoff(this._failures))
      }
    }
  }

  async _streamOnce() {
    this._abort = new AbortController()
    const { signal } = this._abort
    let stallTimer = null
    let ended = false
    let sawFrames = false

    const armStallTimer = () => {
      clearTimeout(stallTimer)
      stallTimer = setTimeout(() => this._abort?.abort(), STALL_TIMEOUT_MS)
    }

    try {
      const res = await this._openStream({ since: this._cursor, signal })
      this._onStatus("open")
      armStallTimer()

      const parser = new SseFrameParser((frame) => {
        sawFrames = true
        if (frame.event === "hello" || frame.event === "changes") this._apply(frame.data)
        if (frame.event === "expired" || frame.event === "bye") {
          ended = true
          this._abort?.abort()
        }
      })

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        armStallTimer()
        parser.push(decoder.decode(value, { stream: true }))
        if (ended) break
      }
      return { serverEnded: ended, sawFrames }
    } catch (err) {
      // Abort nach expired/bye ist ein reguläres Ende, kein Fehler.
      if (!ended) throw err
      return { serverEnded: true, sawFrames }
    } finally {
      clearTimeout(stallTimer)
      this._abort = null
    }
  }

  async _pollFallback() {
    this._onStatus("polling")
    let polls = 0
    while (this._running && polls < SSE_RETRY_AFTER_POLLS) {
      await this._sleep(FALLBACK_POLL_MS)
      if (!this._running) return
      if (this._kicked) {
        // kick() beendet den Fallback -> sofortiger SSE-Versuch im Loop
        this._kicked = false
        this._failures = 0
        return
      }
      try {
        this._apply(await this._getChanges(this._cursor))
      } catch (err) {
        console.warn("Change feed poll failed:", err?.message || err)
      }
      polls += 1
    }
    // periodischer SSE-Retry
    this._failures = 0
  }
}
